/**
 * Allowed values of the Ancestry Review vehicle attributes the versioned symbol table (v2, #34372)
 * stamps on a car and the rater sends as vehicle_type_code / vehicle_horsepower_code /
 * vehicle_symbol_group (#35198). An underwriter entering symbols manually picks from these.
 */
export const vehicleTypeCodes = ['C', 'O', 'P', 'R', 'S', 'X'];
export const vehicleHorsepowerCodes = ['A', 'B', 'C', 'D', 'E', 'X'];
export const vehicleSymbolGroups = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
const toOptions = (codes) => codes.map((code) => ({ id: code, value: code }));
export const vehicleTypeCodeOptions = toOptions(vehicleTypeCodes);
export const vehicleHorsepowerCodeOptions = toOptions(vehicleHorsepowerCodes);
export const vehicleSymbolGroupOptions = toOptions(vehicleSymbolGroups);

const getCurrentCarInitialValue = (initialCars, vin) => {
  return (
    initialCars?.find((car) => car.VIN === vin) || {
      symbolMake: undefined,
      symbolAux: undefined,
      symbolStyle: undefined,
      symbolModel: undefined,
      symbolPGS: undefined,
      symbolBI: undefined,
      symbolPD: undefined,
      symbolMed: undefined,
      symbolPIP: undefined,
      symbolColl: undefined,
      symbolComp: undefined,
      symbolUM: undefined,
      vehicleTypeCode: undefined,
      vehicleHorsepowerCode: undefined,
      convertible: undefined,
      vehicleSymbolGroup: undefined
    }
  );
};

export const hasMakeModelStyleSymbols = (car) => {
  return car.symbolMake || car.symbolModel || car.symbolStyle;
};

/**
 * The Verisk/RAPA rating symbols a farm-carrier auto (e.g. CFM) sends to ClarionDoor, formatted for
 * display (#35042). Those cars rate on a liability symbol pair (`rapaLiabilitySymbol1/2`) and a
 * physical-damage symbol pair (`rapaPhysDamSymbol1/2`) — each the two halves of one 2-char symbol —
 * NOT the Branch make/model/style scheme. Returns `{ liability, physicalDamage }` (e.g.
 * `{ liability: 'LK', physicalDamage: 'XM' }`), or `null` when this is a Branch auto, so callers can
 * fall back to the make/model display.
 *
 * The values mirror exactly what the CFM auto rater sends (see packages/clarion-door-rating
 * .../carriers/cfm/raters/auto/answers-handlers/vehicle-answers.handler.ts): an antique always rates on
 * 'A' regardless of its stored symbols, and any half with no stored symbol defaults to 'A'. So a
 * symbol-less imported car on a RAPA brand shows 'AA'/'AA' — the rater's default — instead of the
 * empty Branch make/model inputs, which don't apply to it.
 *
 * @param {object} car
 * @param {{ usesRapaSymbols?: boolean }} [opts] `usesRapaSymbols` — the brand rates auto on RAPA
 *   symbols (a farm carrier such as CFM), so show the RAPA display even when the car stored none.
 */
export const getRapaRatingSymbols = (car, { usesRapaSymbols = false } = {}) => {
  const c = car ?? {};
  const hasAnyRapaSymbol = !!(
    c.rapaLiabilitySymbol1 ||
    c.rapaLiabilitySymbol2 ||
    c.rapaPhysDamSymbol1 ||
    c.rapaPhysDamSymbol2
  );
  if (!usesRapaSymbols && !hasAnyRapaSymbol) {
    return null;
  }
  const half = (value) => (c.isAntique ? 'A' : value || 'A');
  return {
    liability: `${half(c.rapaLiabilitySymbol1)}${half(c.rapaLiabilitySymbol2)}`,
    physicalDamage: `${half(c.rapaPhysDamSymbol1)}${half(c.rapaPhysDamSymbol2)}`
  };
};

const normalizedSymbol = (value) => value?.toUpperCase() || undefined;

export const haveAnyOfThisCarsSymbolsChanged = (currentCar, initialCars) => {
  const initialCarValue = getCurrentCarInitialValue(initialCars, currentCar.VIN);
  return (
    initialCarValue.symbolMake !== (currentCar.symbolMake?.toUpperCase() || undefined) ||
    initialCarValue.symbolModel !== (currentCar.symbolModel?.toUpperCase() || undefined) ||
    initialCarValue.symbolStyle !== (currentCar.symbolStyle?.toUpperCase() || undefined) ||
    initialCarValue.symbolAux !== (currentCar.symbolAux?.toUpperCase() || undefined) ||
    initialCarValue.symbolPGS !== (currentCar.symbolPGS?.toUpperCase() || undefined) ||
    initialCarValue.symbolBI !== currentCar.symbolBI ||
    initialCarValue.symbolPD !== currentCar.symbolPD ||
    initialCarValue.symbolMed !== currentCar.symbolMed ||
    initialCarValue.symbolPIP !== currentCar.symbolPIP ||
    initialCarValue.symbolColl !== currentCar.symbolColl ||
    initialCarValue.symbolComp !== currentCar.symbolComp ||
    initialCarValue.symbolUM !== currentCar.symbolUM ||
    // Ancestry Review attributes (#35198). The initial car comes from GraphQL, where an absent
    // attribute (a v1-symboled car) is null, so normalize both sides or an untouched car reads as changed.
    normalizedSymbol(initialCarValue.vehicleTypeCode) !== normalizedSymbol(currentCar.vehicleTypeCode) ||
    normalizedSymbol(initialCarValue.vehicleHorsepowerCode) !== normalizedSymbol(currentCar.vehicleHorsepowerCode) ||
    !!initialCarValue.convertible !== !!currentCar.convertible ||
    normalizedSymbol(initialCarValue.vehicleSymbolGroup) !== normalizedSymbol(currentCar.vehicleSymbolGroup)
  );
};
